import { useRef } from "react";
import { Icon } from "@/components/ui/Icon";
import { t } from "@/lib/strings";

interface Props {
  value: string | null;
  onChange: (value: string | null) => void;
}

function formatDE(iso: string): string {
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}.${m}.${y}` : iso;
}

/** Zieltermin control: a bare calendar-icon button when empty (no "tt.mm.jjjj"
 * placeholder), expanding into a blue date pill once a date is set. The native
 * picker is opened via showPicker() off a visually-hidden input. */
export function GoalDateControl({ value, onChange }: Props) {
  const ref = useRef<HTMLInputElement>(null);

  function openPicker(e: React.MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    const el = ref.current as (HTMLInputElement & { showPicker?: () => void }) | null;
    if (el?.showPicker) el.showPicker();
    else el?.focus();
  }

  return (
    <span
      style={{ display: "inline-flex", alignItems: "center", position: "relative" }}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        ref={ref}
        type="date"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        tabIndex={-1}
        aria-label={t.goalDateLabel}
        style={{
          position: "absolute",
          left: 0,
          bottom: 0,
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: "none",
          border: "none",
          padding: 0,
        }}
      />
      {value ? (
        <button
          type="button"
          onClick={openPicker}
          title={t.goalDateLabel}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "3px 10px",
            borderRadius: 999,
            border: "1px solid var(--accent)",
            background: "var(--surface-soft)",
            color: "var(--accent)",
            fontSize: 12,
            fontWeight: 500,
            cursor: "pointer",
            fontFamily: "inherit",
            whiteSpace: "nowrap",
          }}
        >
          <Icon name="date" size={13} />
          {formatDE(value)}
        </button>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          title={t.goalDateSet}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 26,
            height: 26,
            borderRadius: 7,
            border: "1px solid var(--hairline)",
            background: "transparent",
            color: "var(--ink-4)",
            cursor: "pointer",
          }}
        >
          <Icon name="date" size={14} />
        </button>
      )}
    </span>
  );
}
